import { useState, type MouseEvent, type ReactNode } from 'react';
import { Avatar, Box, Flex, Text, Tooltip } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons';
import {
  ProductAreaHeaderIcon,
  StatusHeaderIcon,
} from '../../../icons/x-symbol-svgrepo-com';
import type { ExploreIdeaListRowData } from '../hooks/useExploreIdeasListPage';
import { isValidMongoObjectId } from '../utils/resolve-explore-idea-ids';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { useFields } from '../../../hooks/useFields';
import { buildComponentHierarchyPath } from '../../general/BagelAccordionMenu';

const SKIP_ROW_CLICK_ATTR = 'data-explore-ideas-skip-row-click';
export const ATTRIBUTE_BORDER = '#D8D8DB';
export const ATTRIBUTE_TEXT = '#585C68';
export const ATTRIBUTE_BG = '#FFFFFF';
const PILL_BORDER_RADIUS = 6;
const SELECTED_ROW_BG = '#EFEFFD';
const SELECTED_ROW_HOVER_BG = '#E6E6FC';
const IDEA_ROW_TITLE_FONT_SIZE = 16;
const IDEA_ROW_IMPACTS_FONT_SIZE = 14;

function buildOwnerInitials(ownerName: string, ownerInitials: string): string {
  if (ownerInitials.trim()) {
    return ownerInitials.trim().slice(0, 2).toLowerCase();
  }
  const parts = ownerName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toLowerCase();
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toLowerCase();
}

export function ExploreIdeaAttributeTooltip({
  typeLabel,
  value,
  content,
  children,
}: {
  typeLabel: string;
  value: string;
  content?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Tooltip
      withinPortal
      withArrow
      label={
        content ?? (
          <Box p={4}>
            <Text fz={11.4} mb={4}>
              {typeLabel}:
            </Text>
            <Text fz={11.4}>{value}</Text>
          </Box>
        )
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', maxWidth: '100%' }}>
        {children}
      </Box>
    </Tooltip>
  );
}

export function ExploreIdeaAttributePill({
  label,
  icon,
  tooltipType,
  tooltipContent,
  showEllipsisPrefix,
}: {
  label: string;
  icon: ReactNode;
  tooltipType: string;
  tooltipContent?: ReactNode;
  showEllipsisPrefix?: boolean;
}) {
  return (
    <ExploreIdeaAttributeTooltip
      typeLabel={capitalizeFirstLetter(tooltipType)}
      value={label}
      content={tooltipContent}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          height: 24,
          paddingInline: 8,
          borderRadius: PILL_BORDER_RADIUS,
          border: `1px solid ${ATTRIBUTE_BORDER}`,
          backgroundColor: ATTRIBUTE_BG,
          color: ATTRIBUTE_TEXT,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          flexShrink: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Text component="span" size="xs" fw={500} c={ATTRIBUTE_TEXT} lineClamp={1} sx={{ fontSize: 12 }}>
          {showEllipsisPrefix && (
            <Text component="span" c="dimmed">
              .../{' '}
            </Text>
          )}
          {label}
        </Text>
      </Box>
    </ExploreIdeaAttributeTooltip>
  );
}

function ExploreIdeaInlineMetric({
  value,
  label,
  tooltipLabel,
}: {
  value: string;
  label: string;
  tooltipLabel: string;
}) {
  return (
    <ExploreIdeaAttributeTooltip typeLabel={capitalizeFirstLetter(tooltipLabel)} value={value}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          lineHeight: 1.2,
        }}
      >
        <Text
          component="span"
          fw={700}
          c="#212529"
          sx={{ fontSize: IDEA_ROW_IMPACTS_FONT_SIZE, whiteSpace: 'nowrap' }}
        >
          {value}
        </Text>
        <Text
          component="span"
          fw={400}
          c="#868E96"
          sx={{ fontSize: IDEA_ROW_IMPACTS_FONT_SIZE, whiteSpace: 'nowrap' }}
        >
          {label}
        </Text>
      </Box>
    </ExploreIdeaAttributeTooltip>
  );
}

function ExploreIdeaRowMetrics({ row }: { row: ExploreIdeaListRowData }) {
  const metricItems = row.impactMetrics.map((metric) => ({
    key: metric.key,
    value: metric.formattedValue,
    label: metric.label,
  }));

  return (
    <Flex align="center" gap={6} sx={{ flexShrink: 0, minWidth: 0 }}>
      {metricItems.map((item, index) => (
        <Flex key={item.key} align="center" gap={6} sx={{ flexShrink: 0 }}>
          {index > 0 ? (
            <Text
              component="span"
              c="#CED4DA"
              sx={{ fontSize: IDEA_ROW_IMPACTS_FONT_SIZE, lineHeight: 1 }}
            >
              ·
            </Text>
          ) : null}
          <ExploreIdeaInlineMetric
            value={item.value}
            label={item.label}
            tooltipLabel={item.label}
          />
        </Flex>
      ))}
    </Flex>
  );
}

export interface ExploreIdeaListRowProps {
  row: ExploreIdeaListRowData;
  onRowClick: (ideaId: string) => void;
  isSelected?: boolean;
}

export function ExploreIdeaListRow({ row, onRowClick, isSelected = false }: ExploreIdeaListRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { allComponents } = useFields();

  const handleOpenIdeaPage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isValidMongoObjectId(row.id)) {
      return;
    }
    window.open(`/idea/${row.id}`, '_blank', 'noopener,noreferrer');
  };

  const displayTitle = row.isMissing ? 'Idea unavailable' : row.title;
  const statusLabel = row.statusLabel?.trim() || 'Unassigned';
  const componentLabel = row.componentName?.trim() || 'Unassigned';
  const ownerInitials = buildOwnerInitials(row.ownerName, row.ownerInitials);

  return (
    <Box
      py="sm"
      px="md"
      role="button"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (isValidMongoObjectId(row.id)) {
          onRowClick(row.id);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (isValidMongoObjectId(row.id)) {
            onRowClick(row.id);
          }
        }
      }}
      sx={{
        borderBottom: '1px solid #ECECEF',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        backgroundColor: isSelected ? SELECTED_ROW_BG : 'transparent',
        '&:hover': {
          backgroundColor: isSelected ? SELECTED_ROW_HOVER_BG : '#F8F9FA',
        },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Flex justify="space-between" align="center" gap="md" wrap="nowrap" sx={{ minHeight: 56 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Tooltip label={displayTitle} withinPortal withArrow openDelay={300} multiline maw={360}>
            <Text
              fw={600}
              lineClamp={1}
              c={row.isMissing ? 'dimmed' : '#212529'}
              mb={6}
              sx={{ fontSize: IDEA_ROW_TITLE_FONT_SIZE, lineHeight: 1.25 }}
            >
              {displayTitle}
            </Text>
          </Tooltip>
          <Flex align="center" gap={8} wrap="wrap" sx={{ minHeight: 24 }}>
            <ExploreIdeaAttributePill
              label={statusLabel}
              icon={<StatusHeaderIcon />}
              tooltipType="status"
            />
            <ExploreIdeaAttributePill
              label={componentLabel}
              icon={<ProductAreaHeaderIcon />}
              tooltipType="product area"
              tooltipContent={buildComponentHierarchyPath(row.componentObj, allComponents)}
              showEllipsisPrefix={Boolean(componentLabel && componentLabel !== 'Unassigned')}
            />
            {row.ownerName ? (
              <ExploreIdeaAttributeTooltip typeLabel="Owner" value={row.ownerName}>
                <Flex align="center" gap={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                  <Avatar
                    radius="xl"
                    size={22}
                    sx={{
                      border: `1px solid ${ATTRIBUTE_BORDER}`,
                      backgroundColor: '#F1F3F5',
                      color: ATTRIBUTE_TEXT,
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'lowercase',
                    }}
                  >
                    {ownerInitials || '?'}
                  </Avatar>
                  <Text size="xs" c={ATTRIBUTE_TEXT} lineClamp={1} sx={{ fontSize: 12 }}>
                    {row.ownerName}
                  </Text>
                </Flex>
              </ExploreIdeaAttributeTooltip>
            ) : null}
          </Flex>
        </Box>

        <Box sx={{ position: 'relative', flexShrink: 0, minWidth: 0, display: 'inline-flex' }}>
          <ExploreIdeaRowMetrics row={row} />
          <Tooltip label="Open idea page" withArrow position="left" withinPortal>
            <Box
              {...{ [SKIP_ROW_CLICK_ATTR]: '' }}
              onClick={handleOpenIdeaPage}
              onMouseDown={(event) => event.stopPropagation()}
              aria-label="Open idea page"
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 28,
                borderRadius: 6,
                opacity: isHovered ? 1 : 0,
                pointerEvents: isHovered ? 'auto' : 'none',
                transition: 'opacity 0.12s ease',
                color: '#5C5CEB',
                backgroundColor: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: isHovered ? '0 0 0 1px rgba(216, 216, 219, 0.8)' : 'none',
              }}
            >
              <IconExternalLink size={18} stroke={1.5} />
            </Box>
          </Tooltip>
        </Box>
      </Flex>
    </Box>
  );
}

export default ExploreIdeaListRow;
